import { Map, List } from 'immutable';
import {
    isQueueKey,
    isChannelKey,
    isChatUserKey,
    isConferenceKey,
} from 'flow/utils';
import {
    calcTotalUnreadVoiceMessages,
    extensionStatusIsInCallOrInExternalCall,
} from 'flow/utils/extension-utils';
import {
    getContactName,
    getContactDepartment,
    getContactCompanyName,
} from 'flow/utils/contact-utils';
import {
    convertNumberToE164,
    prettyFormatE164Number,
} from 'flow/utils/call-utils';

export const getWelcomeSounds = (queue, sounds, defaultSounds) => {
    const defaultWelcomeSounds = getDefaultSoundsFromKey(
        defaultSounds,
        queue.getIn(['language', 'key']),
    );

    if (defaultWelcomeSounds) {
        return sounds
            .concat(defaultWelcomeSounds.valueSeq())
            .toOrderedSet()
            .toList();
    } else {
        return sounds;
    }
};

const getDefaultSoundsFromKey = (defaultSounds, languageKey) => {
    if (defaultSounds) {
        return defaultSounds.get(languageKey);
    }
    return null;
};

export const getQueueChannelDescription = (profileList = List(), i18next) => {
    const listExistsOnProfile = (list) => !!list && !list.isEmpty();
    const isMemberOfChannel = profileList.some((profile) =>
        listExistsOnProfile(profile.get('channelLoginList')),
    );
    const isMemberOfQueue = profileList.some((profile) =>
        listExistsOnProfile(profile.get('queueLoginList')),
    );

    if (
        (isMemberOfChannel && isMemberOfQueue) ||
        (!isMemberOfChannel && !isMemberOfQueue)
    ) {
        return i18next.t('settings.queue_channel_login');
    }
    return isMemberOfChannel
        ? i18next.t('settings.channel_login')
        : i18next.t('settings.queue_login');
};

export const filterQueuesByMember = (queues, allQueueMembers, extensionKey) => {
    if (!extensionKey || !queues || !allQueueMembers) {
        return Map();
    }
    return queues.filter((queue) =>
        allQueueMembers
            ?.get(queue.get('key'))
            ?.some((member) => member.get('extensionKey') === extensionKey),
    );
};

export const filterChannelsByMember = (channels, chatUserKey) => {
    if (!isChatUserKey(chatUserKey)) {
        throw new Error('This method requires a chatUserKey to be provided');
    }
    if (!channels) {
        return Map();
    }
    const memberIsMe = (member) => member.get('chatUserKey') === chatUserKey;
    return channels.filter((channel) => {
        return channel.get('channelMemberDTOs', List()).some(memberIsMe);
    });
};

export const extractPbxIndex = (service) => {
    if (service) {
        const description = service.get('description');
        const number =
            service.getIn(['number', 'nationalFormat'], '') +
            ' ' +
            service.getIn(['number', 'number'], '');
        // The keywords an admin gave the service, which is what a receptionist searches by
        // once the name alone returns 150 hits. Two shapes reach this: queues arrive as the
        // light `sortingData` projection with a flat `keywords`, every other PBX service
        // passes its whole entity with the keywords on its own contact. Read both -- queues
        // are the ones this was built for, and they are the projection.
        // Falsy-guarded rather than defaulted: getIn's default only fires on a missing
        // path, so a present but null keywords field would otherwise put the string "null"
        // in the haystack and match every service. (AC-1943)
        const keywords =
            service.getIn(['contact', 'keywords']) ||
            service.get('keywords') ||
            '';
        return (description + ' ' + number + ' ' + keywords).toLowerCase();
    } else {
        return '';
    }
};

export const filterPbxServiceBySearchableInfo = (
    pbxService,
    filterText = '',
) => {
    if (!pbxService) {
        return null;
    }
    const search = filterText.toLowerCase();

    return pbxService.filter(
        (service) => extractPbxIndex(service).indexOf(search) !== -1,
    );
};

export const filterVideoConferenceBySearchableInfo = (
    videoConference,
    filterText = '',
) => {
    if (!videoConference) {
        return null;
    }
    if (!filterText) {
        // If no search has been made, show all items
        return true;
    }
    const videoString = 'Video conference'.toLowerCase();
    return videoString.indexOf(filterText.toLowerCase()) !== -1;
};

export const filterChannelsBySearchableInfo = (channels, filterText = '') => {
    if (!channels) {
        return null;
    }
    const search = filterText.toLowerCase();

    return channels.filter(
        (channel) =>
            channel.get('channelName', '').toLowerCase().indexOf(search) !== -1,
    );
};

export const filterQueueFavoritesBySearchableInfo = (
    favorites,
    filterText = '',
) => {
    if (!favorites) {
        return null;
    }
    const search = filterText.toLowerCase();

    //Do not display favorites when searching for a service for you will get duplicates
    if (search && search.length > 0) {
        return Map();
    }

    return favorites.filter(
        (favorite) =>
            extractPbxIndex(favorite.get('favoriteData')).indexOf(search) !==
            -1,
    );
};

export const calcTotalUnreadVoiceMessagesForVoicemails = (voicemails) => {
    let count = 0;

    voicemails?.forEach((vm) => {
        const voiceMessages = vm.get('voiceMessages', List());
        count += calcTotalUnreadVoiceMessages(voiceMessages);
    });

    return count;
};

/**
 * Derive the sum of unread messages.
 *
 * @param List[ChatSessionDTO]
 * @return the total number of unread messages or 0.
 */
export const calcTotalUnreadChannelSessionMessages = (sessions) => {
    return sessions.reduce(
        (acc, session) => acc + session.get('numberOfUnreadMessages', 0),
        0,
    );
};

/**
 * Derives the sum of unread channel messages.
 * Returns 0 if the user is a member of the channel but not logged in.
 *
 * @param List[channelDTO] channels
 * @param String chatUserKey
 */
export const calcTotalUnreadChannelMessages = (channels, chatUserKey) => {
    const skipLoginCheck = !chatUserKey;
    if (channels) {
        return channels.reduce((acc, channel) => {
            if (skipLoginCheck || loggedInToChannel(channel, chatUserKey)) {
                return acc + channel.get('numberOfUnreadMessages', 0);
            } else {
                return acc;
            }
        }, 0);
    }
    return 0;
};

const loggedInToChannel = (channel, chatUserKey) => {
    const isLoggedIn = (channelMemberDTO) =>
        channelMemberDTO.get('chatUserKey') === chatUserKey &&
        channelMemberDTO.get('loggedIn');
    return channel.get('channelMemberDTOs', List()).some(isLoggedIn);
};

export const userIsLoggedInToQueue = (extensionKey, queueMembers) => {
    try {
        return (
            queueMembers?.some(
                (member) =>
                    member.get('extensionKey') === extensionKey &&
                    member.get('loggedIn'),
            ) || false
        );
    } catch (_error) {
        return false;
    }
};

export const userIsMemberOfQueue = (extensionKey, queueMembers) => {
    try {
        return (
            queueMembers?.some(
                (member) => member.get('extensionKey') === extensionKey,
            ) || false
        );
    } catch (_error) {
        return false;
    }
};

export const getBgColorFromKey = (entityKey) => {
    if (isQueueKey(entityKey)) {
        return 'bg-pbx-huntgroup';
    } else if (isChannelKey(entityKey)) {
        return 'bg-pbx-channel';
    } else if (isConferenceKey(entityKey)) {
        return 'bg-pbx-conference';
    } else {
        return '';
    }
};

export const getTextColorFromKey = (entityKey) => {
    if (isQueueKey(entityKey)) {
        return 'text-pbx-huntgroup';
    } else if (isChannelKey(entityKey)) {
        return 'text-pbx-channel';
    } else if (isConferenceKey(entityKey)) {
        return 'text-pbx-conference';
    } else {
        return '';
    }
};

export const memberIsLoggedIn = (member) => {
    return member.get('loggedIn') === true;
};

export const getLoggedInMemberSize = (memberList) => {
    return memberList
        ? memberList.filter((member) => memberIsLoggedIn(member)).size
        : 0;
};

export const parseQueueKeyFromTop = (key) => {
    return key && key.replace('_', '-');
};

/**
 * This method is to be used for the queueCalls that are either waiting or ongoing
 */
export const getQueueCallDisplayText = (
    number,
    contact,
    loggedInUserCountryCode,
    i18next,
    textToIgnore = undefined,
) => {
    const contactName = getContactName(contact);
    if (contactName && contactName !== textToIgnore) {
        return contactName;
    }

    const departmentName = getContactDepartment(contact);
    if (departmentName && departmentName !== textToIgnore) {
        return departmentName;
    }

    const contactCompanyName = getContactCompanyName(contact);
    if (contactCompanyName && contactCompanyName !== textToIgnore) {
        return contactCompanyName;
    }

    const prettyFormattedNumber = prettyFormatE164Number(
        number,
        loggedInUserCountryCode,
    );
    if (
        prettyFormattedNumber &&
        prettyFormattedNumber !== textToIgnore &&
        prettyFormattedNumber !== 'unknown'
    ) {
        return prettyFormattedNumber;
    }

    return textToIgnore !== i18next.t('status.unknown')
        ? i18next.t('status.unknown')
        : '';
};

/**
 * NOTE:
 * This method is to be used specifically for queue calls received from Top, where Swedish numbers are
 * formatted in Swedish national format, and numbers from other countries are formated as 00XX (or potentially +XX...)
 *
 * @param data: queue call represented by a JavaScript object
 * @returns a JavaScript object where Source and/or Destination and/or OriginalCallerID are converted to e164 format
 */
export const getTopQueueCallWithNumbersInE164Format = (data) => {
    if (!data) {
        return {};
    }

    if (data.Source) {
        data.Source = convertNumberToE164(data.Source);
    }

    if (data.Destination) {
        data.Destination = convertNumberToE164(data.Destination);
    }

    if (data.OriginalCallerID) {
        data.OriginalCallerID = convertNumberToE164(data.OriginalCallerID);
    }

    return data;
};

const getQueueIsOpen = (queue) => {
    if (!queue) return false;
    return queue.getIn(['activeProfile', 'available']) === true;
};

export const filterExtensionsLoggedInInOneOrManyOpenQueues = (
    extensions,
    queues,
    allQueueMembers,
) => {
    if (!queues || !extensions) {
        return undefined;
    }

    let queueMembersList = List();
    queues.forEach((queue) => {
        if (!getQueueIsOpen(queue)) return;

        const queueMembers = allQueueMembers.get(queue.get('key'), List());
        queueMembers.forEach((member) => {
            queueMembersList = queueMembersList.push(member);
        });
    });

    const extensionsLoggedIn = extensions.filter((ext) => {
        return queueMembersList.find(
            (member) =>
                member.get('extensionKey') === ext.get('key') &&
                member.get('loggedIn'),
        );
    });

    return extensionsLoggedIn;
};

export const getUniqueExtensionKeysForUsersLoggedInToOneOrManyOpenQueuesProvided =
    (queues, allQueueMembers) => {
        if (!queues) return undefined;

        let extensionKeysMap = Map();
        queues.forEach((queue) => {
            if (!getQueueIsOpen(queue)) return;

            const queueMembers = allQueueMembers.get(queue.get('key'), List());
            queueMembers.forEach((member) => {
                const memberExtensionKey = member.get('extensionKey');
                if (memberIsLoggedIn(member) && memberExtensionKey) {
                    extensionKeysMap = extensionKeysMap.set(
                        memberExtensionKey,
                        memberExtensionKey,
                    );
                }
            });
        });

        return extensionKeysMap;
    };

export const filterExtensionKeysInCallOrInExternalCall = (
    extensionKeys,
    statusesMappedByExtensionKey,
) => {
    return extensionKeys.filter((extensionKey) => {
        const status = statusesMappedByExtensionKey.get(extensionKey);
        return extensionStatusIsInCallOrInExternalCall(status);
    });
};

export const filterExtensionKeysNotInCallAndNotInExternalCall = (
    extensionKeys,
    statusesMappedByExtensionKey,
) => {
    return extensionKeys.filter((extensionKey) => {
        const status = statusesMappedByExtensionKey.get(extensionKey);
        return !extensionStatusIsInCallOrInExternalCall(status);
    });
};

export const getUniqueExtensionKeysForQueueMembersInOpenQueuesProvided = (
    queues,
    allQueueMembers,
) => {
    if (!queues) return undefined;

    let extensionKeys = Map();
    queues?.forEach((queue) => {
        if (!getQueueIsOpen(queue)) return;

        const queueMembers = allQueueMembers?.get(queue.get('key'), List());
        queueMembers?.forEach((member) => {
            const memberExtensionKey = member?.get('extensionKey');
            if (memberExtensionKey) {
                extensionKeys = extensionKeys.set(
                    memberExtensionKey,
                    memberExtensionKey,
                );
            }
        });
    });

    return extensionKeys;
};

export const getQueueMember = (extensionKey, queueMembers) => {
    if (queueMembers) {
        return queueMembers.find(
            (member) => member.get('extensionKey') === extensionKey,
        );
    } else {
        return null;
    }
};
